import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  FaSyncAlt, FaPlus, FaArrowLeft, FaExclamationTriangle, FaUserSlash, FaComments, FaExternalLinkAlt, FaFlask,
} from 'react-icons/fa';
import ConversationList from './ConversationList';
import MessageThread from './MessageThread';
import MessageComposer from './MessageComposer';
import NewConversationModal from './NewConversationModal';
import { conversationTitle, formatAuPhone } from './conversationFormat';
import { usePolling, useLatestRequest } from '../../hooks/useConversations';
import { useAppConfig } from '../../context/AppConfigContext';
import {
  getConversations,
  getConversationMessages,
  markConversationRead,
  sendConversationMessage,
  getRecipients,
} from '../../services/messagingService';

// Two-way SMS with franchisees (ticket 71). Inbound replies land on the API via the Telnyx webhook,
// so this polls: the list on a slow cadence, the open thread on a faster one. Both pause while the
// tab is hidden (see usePolling).

const LIST_POLL_MS = 20_000;
const THREAD_POLL_MS = 10_000;

// A thread the admin has opened for a franchisee who has never texted in. It has no server id until
// the first message is sent, so it is held here rather than in the list.
const draftFor = (franchisee) => ({
  id: null,
  franchiseeId: franchisee.id,
  franchiseeName: [franchisee.firstName, franchisee.lastName].filter(Boolean).join(' ').trim()
    || franchisee.franchiseName || franchisee.fseCode,
  fseCode: franchisee.fseCode,
  phoneE164: franchisee.phoneNumber,
  isKnownSender: true,
  unreadCount: 0,
  optedOut: false,
});

const ConversationsTab = ({ onUnreadChange }) => {
  // The open thread lives in the URL, not in state: ?c=<conversationId> for a real thread and
  // ?to=<franchiseeId> for one that has not been started yet. That makes a thread linkable, survives a
  // refresh, and gives browser Back the behaviour operators expect, with no second copy to desync.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('c');
  const draftFranchiseeId = searchParams.get('to');

  const [conversations, setConversations] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [activeConversation, setActiveConversation] = useState(null); // server copy of the open thread
  const [messages, setMessages] = useState([]);
  const [messagesTruncated, setMessagesTruncated] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [franchisees, setFranchisees] = useState([]);
  const [newOpen, setNewOpen] = useState(false);

  const { config } = useAppConfig();
  const mockMode = config?.smsMockMode === true;

  // Destructured so the callbacks below depend on the stable `run`, never on the hook's return object.
  const { run: runListRequest, loading: listLoading } = useLatestRequest();
  const { run: runThreadRequest, loading: threadLoading } = useLatestRequest();

  // Held in a ref so the loaders stay referentially stable even if a parent passes an inline callback.
  // One of those loaders drives a useEffect, so an unstable dependency here means an infinite loop.
  const onUnreadChangeRef = useRef(onUnreadChange);
  onUnreadChangeRef.current = onUnreadChange;
  const reportUnread = useCallback((total) => onUnreadChangeRef.current?.(total), []);

  const updateParams = useCallback((mutate, options) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      mutate(params);
      return params;
    }, options);
  }, [setSearchParams]);

  // ─── Loading ──────────────────────────────────────────────────────────────────
  const loadConversations = useCallback(async ({ silent = true } = {}) => {
    const result = await runListRequest(() => getConversations(), { silent });
    if (!result) return;
    setConversations(result.conversations);
    setTruncated(result.truncated);
    setError(null);
    reportUnread(result.totalUnread);
  }, [runListRequest, reportUnread]);

  const loadMessages = useCallback(async (conversationId, { silent = true } = {}) => {
    if (!conversationId) {
      setMessages([]);
      setMessagesTruncated(false);
      setActiveConversation(null);
      return;
    }
    const result = await runThreadRequest(() => getConversationMessages({ conversationId }), { silent });
    if (!result) return;
    setMessages(result.messages);
    setMessagesTruncated(result.truncated);
    setActiveConversation(result.conversation);

    // Reading the thread clears its badge. Driven by the server's count rather than the list row, so a
    // deep link works before the list has loaded, and a reply arriving while the thread is open is
    // cleared by the next poll instead of sitting there unread in front of the operator.
    if (result.conversation?.unreadCount > 0) {
      setConversations((current) => {
        const next = current.map((c) => (c.id === conversationId ? { ...c, unreadCount: 0 } : c));
        reportUnread(next.reduce((sum, c) => sum + (c.unreadCount || 0), 0));
        return next;
      });
      markConversationRead(conversationId).catch(() => {});
    }
  }, [runThreadRequest, reportUnread]);

  useEffect(() => {
    loadConversations({ silent: false }).catch((e) => setError(e.message || 'Failed to load conversations.'));
    // The picker list is small and only used by the new-conversation modal, so fetch it once.
    getRecipients().then(({ franchisees: list }) => setFranchisees(list)).catch(() => setFranchisees([]));
    // Deliberately mount-only: polling below keeps both fresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  usePolling(() => loadConversations().catch(() => {}), LIST_POLL_MS);
  usePolling(() => loadMessages(selectedId).catch(() => {}), THREAD_POLL_MS, !!selectedId);

  // Whenever the URL names a thread, load it. This is the single place a thread gets opened, so a
  // click, a deep link, and browser Back all take exactly the same path.
  useEffect(() => {
    setError(null);
    setMessages([]);
    loadMessages(selectedId, { silent: false }).catch((e) => setError(e.message || 'Failed to load messages.'));
  }, [selectedId, loadMessages]);

  // ─── Selection ────────────────────────────────────────────────────────────────
  const openConversation = useCallback((conversation) => {
    updateParams((params) => {
      params.set('c', conversation.id);
      params.delete('to');
    });
  }, [updateParams]);

  const startNewConversation = (franchisee) => {
    setNewOpen(false);
    // If they already have a thread, open the real one rather than a duplicate draft.
    const existing = conversations.find((c) => c.franchiseeId === franchisee.id);
    updateParams((params) => {
      if (existing) {
        params.set('c', existing.id);
        params.delete('to');
      } else {
        params.set('to', franchisee.id);
        params.delete('c');
      }
    });
  };

  const clearSelection = () => updateParams((params) => {
    params.delete('c');
    params.delete('to');
  });

  // ─── Sending ──────────────────────────────────────────────────────────────────
  // Rethrows so the composer can surface the reason and keep the typed text. The API records a failed
  // send on the thread too, so the transcript is refreshed either way rather than waiting for the poll.
  const handleSend = async (text) => {
    if (!selected) return;
    setSending(true);
    let conversationId = selected.id;
    try {
      const result = await sendConversationMessage({
        conversationId: selected.id || undefined,
        franchiseeId: selected.id ? undefined : selected.franchiseeId,
        text,
      });
      conversationId = result?.conversationId || selected.id;
    } finally {
      setSending(false);
      if (conversationId && conversationId !== selectedId) {
        // The draft just became a real thread. Replace rather than push, so Back does not return to a
        // draft that no longer exists; the effect above then loads the thread.
        updateParams((params) => {
          params.set('c', conversationId);
          params.delete('to');
        }, { replace: true });
        loadConversations().catch(() => {});
      } else if (conversationId) {
        await Promise.all([
          loadMessages(conversationId, { silent: false }).catch(() => {}),
          loadConversations().catch(() => {}),
        ]);
      }
    }
  };

  // ─── Derived ──────────────────────────────────────────────────────────────────
  // Derived, never stored: the URL says which thread, the server copy fills in the detail, and the list
  // row covers the gap before the thread request comes back.
  const selected = useMemo(() => {
    if (selectedId) {
      if (activeConversation?.id === selectedId) return activeConversation;
      return conversations.find((c) => c.id === selectedId) || null;
    }
    if (draftFranchiseeId) {
      const franchisee = franchisees.find((f) => f.id === draftFranchiseeId);
      return franchisee ? draftFor(franchisee) : null;
    }
    return null;
  }, [selectedId, draftFranchiseeId, activeConversation, conversations, franchisees]);

  const unreadCount = useMemo(
    () => conversations.filter((c) => c.unreadCount > 0).length,
    [conversations]
  );

  const visibleConversations = useMemo(() => {
    const query = search.trim().toLowerCase();
    // The open thread stays listed even once it is read, so it never vanishes from under the selection.
    const base = filter === 'unread'
      ? conversations.filter((c) => c.unreadCount > 0 || c.id === selectedId)
      : conversations;
    if (!query) return base;
    return base.filter((c) =>
      [c.franchiseeName, c.fseCode, c.phoneE164, c.lastMessageText]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [conversations, search, filter, selectedId]);

  const title = selected ? conversationTitle(selected) : '';
  const hasNumber = !!selected?.phoneE164;

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      {mockMode && (
        <div className="px-4 py-2 bg-violet-50 border-b border-violet-200 text-sm text-violet-900 flex items-center gap-2">
          <FaFlask className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            <strong>Mock mode.</strong> Messages are recorded and threaded but never sent to Telnyx, and nothing is billed.
          </span>
        </div>
      )}
      {error && (
        <div className="px-4 py-3 bg-red-50 border-b border-red-200 text-sm text-red-800" role="alert">
          {error}
        </div>
      )}

      {/* Two panes on desktop; on mobile the thread replaces the list so neither is cramped. */}
      <div className="flex h-[calc(100vh-18rem)] min-h-[26rem]">
        <aside
          className={`w-full md:w-80 lg:w-96 shrink-0 border-r border-gray-200 flex-col bg-gray-50 ${
            selected ? 'hidden md:flex' : 'flex'
          }`}
          aria-label="Conversations"
        >
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-200 bg-white">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Conversations</h2>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => loadConversations({ silent: false })}
                disabled={listLoading}
                aria-label="Refresh conversations"
                className="p-2 text-gray-500 hover:text-gray-800 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-jims-cyan disabled:opacity-50"
              >
                <FaSyncAlt className={listLoading ? 'animate-spin' : ''} />
              </button>
              <button
                type="button"
                onClick={() => setNewOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-jims-cyan rounded hover:bg-jims-cyan-dark transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-jims-cyan"
              >
                <FaPlus className="h-3 w-3" /> New
              </button>
            </div>
          </div>

          <ConversationList
            conversations={visibleConversations}
            selectedId={selectedId}
            onSelect={openConversation}
            search={search}
            onSearchChange={setSearch}
            filter={filter}
            onFilterChange={setFilter}
            unreadCount={unreadCount}
            loading={listLoading}
            truncated={truncated}
          />
        </aside>

        <section className={`flex-1 flex-col min-w-0 ${selected ? 'flex' : 'hidden md:flex'}`} aria-label="Message thread">
          {!selected ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6 text-gray-500">
              <FaComments className="h-10 w-10 text-gray-300 mb-3" />
              <p className="font-medium text-gray-600">Select a conversation</p>
              <p className="mt-1 text-sm">Replies from franchisees appear on the left. Use New to start a thread.</p>
            </div>
          ) : (
            <>
              <header className="px-4 py-3 border-b border-gray-200 bg-white">
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    onClick={clearSelection}
                    aria-label="Back to conversations"
                    className="md:hidden p-2 -ml-2 text-gray-500 hover:text-gray-800 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-jims-cyan"
                  >
                    <FaArrowLeft />
                  </button>
                  <div className="min-w-0 flex-1">
                    <h2 className="font-semibold text-gray-900 truncate">{title}</h2>
                    <p className="text-xs text-gray-500 truncate">
                      {formatAuPhone(selected.phoneE164) || 'No number on file'}
                      {selected.fseCode && ` · ${selected.fseCode}`}
                    </p>
                  </div>
                  {selected.franchiseeId && (
                    <Link
                      to={`/franchisees/edit/${selected.franchiseeId}`}
                      className="shrink-0 inline-flex items-center gap-1.5 text-sm font-medium text-jims-cyan hover:text-jims-cyan-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-jims-cyan rounded"
                    >
                      Franchisee <FaExternalLinkAlt className="h-3 w-3" />
                    </Link>
                  )}
                </div>

                {!selected.isKnownSender && (
                  <p className="mt-2 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    <FaUserSlash className="h-3 w-3 mt-0.5 shrink-0" />
                    This number does not match any franchisee record. Check who it belongs to before replying.
                  </p>
                )}
                {selected.optedOut && (
                  <p className="mt-2 flex items-start gap-2 text-xs text-red-800 bg-red-50 border border-red-200 rounded px-3 py-2">
                    <FaExclamationTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                    This number replied STOP. Only send operational messages they have asked for.
                  </p>
                )}
              </header>

              <MessageThread
                messages={messages}
                loading={threadLoading}
                truncated={messagesTruncated}
                emptyHint={selected.id ? undefined : `No messages yet. Your first SMS will start this conversation with ${title}.`}
              />

              <MessageComposer
                onSend={handleSend}
                sending={sending}
                disabled={!hasNumber}
                disabledReason={hasNumber ? null : 'This conversation has no usable mobile number, so a reply cannot be sent.'}
                recipientLabel={title}
              />
            </>
          )}
        </section>
      </div>

      <NewConversationModal
        isOpen={newOpen}
        franchisees={franchisees}
        onSelect={startNewConversation}
        onClose={() => setNewOpen(false)}
      />
    </div>
  );
};

export default ConversationsTab;
